import getFeaturePlansRecommended from 'core/services/getFeaturePlansRecommended'
import { TEMPORAL_DATA_FROM_QUOTE_KEY } from 'core/constants/storage'
import fetchJSONHandleError, {
  type FetchJSONHandleErrorResponse
} from 'utils/NET/fetchJSONHandleError'
import { getQuoteDate } from 'utils/pivot/date/getLastStartDate'
import { BACKEND_PIVOT } from 'core/constants/endpoints'
import getRecEngine from 'core/services/getRecEngine'
import {
  deepClone,
  stringToDate,
  getDifferenceOnDays,
  propAccessor,
  IDictionary
} from '@hc-frontend/utils'
import sessionData from 'core/sessionData'
import { IS_ENROLL, IS_AGENT, IS_FIXED_BENEFIT_MEDICAL } from 'core/constants/environment'
import { ADD_ONS_PLANS } from 'core/constants/products'
import { findChildren } from '@hc-frontend/business'
import { CDN_PIVOT } from 'core/constants/endpoints'
import getURLParams from 'utils/DOM/getURLParams'
import getQuoteByKey from '../getQuoteByKey'
import storeUpdate from 'utils/store/withMiddleware'
import { enableFlexBenefits } from 'utils/experiments/enableFlexBenefits'
import { getQuote } from './getQuote'

function parseMember(member) {
  return {
    dob: member.stringDOB,
    gender: member.gender.toUpperCase(),
    role: member.type.toLowerCase()
  }
}

const getEndDate = (hcStorage, days) => {
  if (days === +hcStorage.period) {
    return ''
  }

  if (hcStorage.endDate) {
    return hcStorage.endDate
  }

  return ''
}

function makeData({
  store,
  products,
  plans,
  quoteKey,
  fetchAlternativeRecommendedPlans,
  applyExternalPlans
}) {
  const queryParams = getURLParams()
  const { hcStorage } = store
  const days = hcStorage.endDate
    ? getDifferenceOnDays(
        stringToDate(hcStorage.sdate)!,
        stringToDate(hcStorage.endDate)!
      )
    : 0

  const quoteDate = getQuoteDate()
  let members = hcStorage.members

  if (IS_FIXED_BENEFIT_MEDICAL && hcStorage.childOnly) {
    members = findChildren(hcStorage.members)
    products = products.filter(productId => !ADD_ONS_PLANS.includes(productId))
  }

  const isMultiCarrier = store.planId
    ? store.plans[store.planId].multiCarrierPlan
    : store.eligibilityMonths > 4 && ['8', '12'].includes(queryParams['months'])

  const baseData = {
    quoteKey,
    agentId: '' + hcStorage.agent_id,
    carrierId: 1,
    location: {
      state: hcStorage.location.state_acronym,
      zip: hcStorage.location.zip_code
    },
    coverage: {
      startDate: hcStorage.sdate,
      endDate: getEndDate(hcStorage, days)
    },
    members: members.map(parseMember),
    products,
    plans,
    quoteDate: quoteDate ? quoteDate : null,
    multiCarrier: store.eligibilityBool,
    email: hcStorage.contact.email_address,
    applyExternalPlans
  }

  const payload = {
    ...baseData,
    healthcareUserId: hcStorage.healthcareUserId,
    healthcareSession: hcStorage.healthcareSession
  }

  const forStorageKey = {
    ...baseData,
    sessionId: propAccessor(hcStorage, 'session.sessionId'),
    isMultiCarrier
  }

  const storageKey = `Plans-${
    IS_ENROLL ? `RANKING${fetchAlternativeRecommendedPlans ? 1 : 0}` : 'PLANE'
  }-${JSON.stringify(deepClone(forStorageKey))}`

  return { payload, storageKey }
}

/**
 * Ensure the recEngine consider the responses from the different sources.
 * The staticRecPlansResponse is used to override the recEngine recommendations when it's available.
 * This is useful since the recEngine service is not available for all application environments.
 *
 * @param store - The current store instance
 * @param recEngineResponse - The fetchJSONHandleError response from the recEngine service
 * @param staticRecPlansResponse - The fetchJSONHandleError response from the static json file with the recommendations
 * @returns
 */

const sanitizeRecEngine = (
  store: Pivot.State,
  recEngineResponse: FetchJSONHandleErrorResponse<Pivot.RecommendationEngine>,
  staticRecPlansResponse: FetchJSONHandleErrorResponse<
    Record<string, Pivot.RecommendationEngine['recommendations']>
  >
) => {
  if (!recEngineResponse && !staticRecPlansResponse) return

  const staticRecommendedPlans =
    staticRecPlansResponse &&
    staticRecPlansResponse[store.hcStorage.location.state_acronym]

  const recEngine = {
    ...recEngineResponse,
    recommendations: staticRecommendedPlans || recEngineResponse.recommendations
  }
  if (staticRecommendedPlans && recEngine.error) delete recEngine.error // remove error from recEngine if we have staticRecommendedPlans

  return recEngine
}

const performNewGetQuoteRequest = ({
  store,
  data,
  temporalData,
  fetchRecEngine,
  fetchAlternativeRecommendedPlans
}) => {
  const services = [
    temporalData
      ? Promise.resolve(temporalData.quoteResponse)
      : getQuote(BACKEND_PIVOT, data),
    fetchRecEngine
      ? getRecEngine(store.hcStorage)
      : getFeaturePlansRecommended(store.hcStorage),
    fetchAlternativeRecommendedPlans
      ? fetchJSONHandleError({
          url: `${CDN_PIVOT}/resources/v2/products/enroll-recommended-plans.json`
        })
      : undefined
  ].filter(Boolean)
  return Promise.all(services).then(([quoteResponse, recEngine, recommendPlans]) => {
    if (quoteResponse.status === 'ERROR') {
      quoteResponse.hasError = true
      return [quoteResponse, recEngine]
    }

    const response = [
      quoteResponse.data,
      sanitizeRecEngine(store, recEngine, recommendPlans)
    ]

    return response
  })
}

const performReloadQuoteRequest = (store, quoteKey, fetchRecEngine) =>
  Promise.all([
    getQuoteByKey(quoteKey),
    fetchRecEngine
      ? getRecEngine(store.hcStorage)
      : getFeaturePlansRecommended(store.hcStorage),
    fetchJSONHandleError({
      url: `${CDN_PIVOT}/resources/v2/products/enroll-recommended-plans.json`
    })
  ]).then(([quoteResponse, recEngineResponse]) => [
    { hasError: !!quoteResponse.error, ...quoteResponse.data },
    recEngineResponse
  ])

function getPlansByProductId(
  { products = [], plans = [], fetchRecEngine = !IS_AGENT, applyGetExternalPlans = true },
  store: Pivot.State
) {
  // If we have a signed pivot state, return the quote from it
  const signedQuote = propAccessor(
    store as unknown as IDictionary<unknown>,
    'signedDetails.quote'
  )
  if (signedQuote) return Promise.resolve(signedQuote)

  // If we have invalid information from quoteKey, return the new start date
  const temporalData = sessionData.getData(TEMPORAL_DATA_FROM_QUOTE_KEY)
  sessionData.setData(TEMPORAL_DATA_FROM_QUOTE_KEY, '')
  if (temporalData && temporalData.nextStartDate) {
    return Promise.resolve([
      {
        hasError: true,
        status: 'ERROR',
        description: 'start date',
        nextStartDate: temporalData.nextStartDate
      }
    ])
  }

  // Set some constants for the ne
  const fetchAlternativeRecommendedPlans = fetchRecEngine

  // Set applyExternalPlans
  const applyExternalPlans = !applyGetExternalPlans
    ? false
    : applyGetExternalPlans && enableFlexBenefits()

  // May retrieve existing quote from the backend service using the query parameter
  const explicitQuoteKey = getURLParams()['quoteKey']
  if (explicitQuoteKey)
    return performReloadQuoteRequest(store, explicitQuoteKey, fetchRecEngine).then(
      response => {
        const { storageKey } = makeData({
          store,
          products,
          plans,
          fetchAlternativeRecommendedPlans,
          quoteKey: explicitQuoteKey,
          applyExternalPlans: applyExternalPlans
        })
        sessionData.setData(storageKey, response)
        return response
      }
    )

  // Hierarchical order to get the quoteKey
  const quoteKey =
    getURLParams()['quoteKey'] || store.quoteKey || store.hcStorage.quoteKey

  // Create the data for the quote request
  const { payload, storageKey } = makeData({
    store,
    products,
    plans,
    quoteKey,
    fetchAlternativeRecommendedPlans,
    applyExternalPlans: applyExternalPlans
  })

  // If there is a quoteKey, try to get the quote from the sessionData
  if (quoteKey) {
    const lastResponse = sessionData.getData(storageKey)
    if (!temporalData && lastResponse) {
      return Promise.resolve(lastResponse)
    }
  }

  // Finally perform the request to create a new getQuote and save the response in sessionData
  return performNewGetQuoteRequest({
    store,
    data: payload,
    temporalData,
    fetchRecEngine,
    fetchAlternativeRecommendedPlans
  }).then(response => {
    const updatedQuoteKey = response[0].quoteKey
    const { storageKey: updatedStorageKey } = makeData({
      store,
      products,
      plans,
      quoteKey: updatedQuoteKey,
      fetchAlternativeRecommendedPlans,
      applyExternalPlans: applyExternalPlans
    })
    storeUpdate.update({ externalPlans: response[0].externalPlans })

    sessionData.setData(updatedStorageKey, response)
    return response
  })
}

export default getPlansByProductId
